<template>
  <div
    class="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
  >
    <div
      class="flex cursor-pointer items-center justify-between p-5"
      @click="facebookExpanded = !facebookExpanded"
    >
      <div class="flex items-center gap-4">
        <!-- Platform Icon -->
        <div
          class="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20"
        >
          <i
            class="fa-brands fa-facebook text-2xl text-blue-600 dark:text-blue-400"
          ></i>
        </div>
        <div>
          <h3 class="text-lg font-medium text-gray-800 dark:text-white">
            Facebook Marketing
          </h3>
          <div class="flex items-center gap-2 mt-1">
            <span
              class="inline-flex h-2 w-2 rounded-full"
              :class="isFacebookConnected ? 'bg-green-500' : 'bg-gray-400'"
            ></span>
            <span
              class="text-sm font-medium"
              :class="
                isFacebookConnected
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
              "
            >
              {{ isFacebookConnected ? "Connected" : "Not Connected" }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <div class="flex items-center">
          <div
            style="cursor: pointer"
            class="relative inline-block w-8 h-6 rounded-full"
          >
            <input
              id="facebookAutoPilotSwitch"
              :disabled="!isFacebookConnected || isProcessingAutoPilot"
              style="cursor: pointer"
              type="checkbox"
              v-model="autoPilotEnabled"
              @change="handleAutoPilotChange"
              class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
            />
            <label
              htmlFor="facebookAutoPilotSwitch"
              style="cursor: pointer"
              class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
            >
              <div
                class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                data-ripple-dark="true"
              ></div>
            </label>
          </div>
          <span
            class="ml-5 text-sm font-medium text-gray-700 dark:text-gray-300"
            >Auto Pilot</span
          >
        </div>
        <button
          class="text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <i
            class="fas"
            :class="facebookExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>
      </div>
    </div>

    <!-- Connection Button (when collapsed) -->
    <div v-if="!facebookExpanded && !isFacebookConnected" class="px-5 pb-5">
      <button
        @click.stop="connectFacebook"
        class="w-full rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:from-blue-700 hover:to-blue-600 transition-all duration-200"
      >
        <i class="fa-brands fa-facebook mr-2"></i> Connect Facebook Account
      </button>
    </div>

    <!-- Expanded Content -->
    <div
      v-if="facebookExpanded"
      class="space-y-6 border-t border-gray-200 p-6 dark:border-gray-600"
    >
      <div v-if="!isFacebookConnected">
        <nav
          class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5"
        >
          <div
            class="w-full max-w-fit flex flex-row gap-5 items-center justify-start"
          >
            <h3 class="text-xl font-medium dark:text-gray-200">
              Facebook Connection
            </h3>
          </div>

          <div
            class="flex flex-row justify-end items-center gap-4 portrait:w-full"
          >
            <button
              @click="connectFacebook"
              class="portrait:w-full rounded-lg border border-main-900 py-3 px-4 text-center text-sm font-semibold text-main-900 transition-all hover:bg-gray-100 dark:border-gray-400 dark:text-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600"
            >
              Connect Facebook Account
            </button>
          </div>
        </nav>
      </div>

      <div v-else class="space-y-6">
        <nav
          class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5"
        >
          <div
            class="w-full max-w-fit flex flex-row gap-5 items-center justify-start"
          >
            <h3
              class="text-base font-medium dark:text-gray-200 flex items-center text-green-600"
            >
              <svg class="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>Connected as {{ userInfo.name }}</span>
            </h3>
          </div>

          <div
            class="flex flex-row justify-end items-center gap-4 portrait:w-full"
          >
            <button
              @click="disconnectAccount"
              :disabled="loading"
              class="portrait:w-full font-semibold text-center text-sm py-3 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-md dark:bg-red-700 dark:hover:bg-red-800 transition-colors flex items-center justify-center gap-2"
            >
              <span
                v-if="loading"
                class="inline-flex items-center gap-2 whitespace-nowrap"
              >
                <svg
                  class="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Disconnecting...
              </span>
              <span v-else> Disconnect Account </span>
            </button>
          </div>
        </nav>

        <!-- Connection Status -->
        <div
          class="bg-white dark:bg-gray-700 rounded-lg shadow p-6 transition-colors"
        >
          <div v-if="!isFacebookConnected" class="space-y-4">
            <p class="dark:text-gray-300">
              Connect your Facebook account to get started with ad automation.
            </p>
            <button
              @click="connectFacebook"
              class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors dark:bg-blue-700 dark:hover:bg-blue-800"
            >
              Connect Facebook Account
            </button>
          </div>
          <div v-else class="space-y-4">
            <h3
              class="text-lg font-medium text-gray-800 dark:text-gray-200 flex items-center"
            >
              <svg
                class="w-5 h-5 mr-2 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clip-rule="evenodd"
                />
              </svg>
              Configuration Checklist
            </h3>

            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div class="divide-y divide-gray-200 dark:divide-gray-700">
                <div
                  v-for="(item, index) in requiredVariables"
                  :key="index"
                  class="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                  :class="{ 'bg-blue-50 dark:bg-blue-900/20': !item.valid }"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center min-w-0">
                      <span
                        class="flex-shrink-0"
                        :class="
                          item.valid ? 'text-green-500' : 'text-yellow-500'
                        "
                      >
                        <svg
                          class="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            v-if="item.valid"
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          />
                          <path
                            v-else
                            fill-rule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </span>

                      <div class="ml-3 min-w-0">
                        <p
                          class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate"
                        >
                          {{ item.label }}
                        </p>
                        <p
                          v-if="item.description"
                          class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                        >
                          {{ item.description }}
                        </p>
                      </div>
                    </div>

                    <div v-if="!item.valid" class="ml-4 flex-shrink-0">
                      <button
                        type="button"
                        @click="item.action"
                        class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800 dark:focus:ring-blue-600 transition-colors duration-200"
                      >
                        Set Up
                        <svg
                          class="ml-1 -mr-0.5 h-4 w-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                    <div v-else class="ml-4 flex-shrink-0">
                      <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                      >
                        Configured
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="incompleteCount > 0"
              class="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-4"
            >
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg
                    class="h-5 w-5 text-yellow-400 dark:text-yellow-300"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm text-yellow-700 dark:text-yellow-300">
                    <span class="font-medium"
                      >{{ incompleteCount }} configuration{{
                        incompleteCount > 1 ? "s" : ""
                      }}
                      remaining</span
                    >
                    - Please complete all required setup steps for full
                    functionality.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Page and Ad Account Selection -->
        <div
          class="bg-white dark:bg-gray-700 rounded-lg shadow p-6 transition-colors"
          v-if="isFacebookConnected"
        >
          <h2 class="text-xl font-bold mb-6 dark:text-gray-200">
            Ad Configuration
          </h2>

          <div class="mb-8">
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-medium text-lg dark:text-gray-200">
                Business Account
              </h3>
              <button
                @click="fetchBusinesses"
                class="text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded-lg transition-colors dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200"
                :disabled="loading"
              >
                Refresh Pages
              </button>
            </div>

            <div v-if="businesses.length > 0" class="space-y-4">
              <div
                v-for="business in businesses"
                :key="business.id"
                @click="selectBusiness(business)"
                class="p-4 border rounded-lg cursor-pointer transition-colors"
                :class="{
                  'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-700':
                    selectedBusiness === business.id,
                  'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500':
                    selectedBusiness !== business.id,
                }"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center"
                  >
                    <i
                      class="fa-solid fa-file-lines text-gray-600 dark:text-gray-300"
                    ></i>
                  </div>
                  <div>
                    <h4 class="font-medium dark:text-gray-200">
                      {{ business.name }}
                    </h4>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      ID: {{ business.id }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="p-4 border border-dashed rounded-lg text-center dark:border-gray-600"
            >
              <p class="text-gray-500 dark:text-gray-400 mb-3">
                No Facebook Pages found
              </p>
              <button
                @click="createFacebookPage"
                class="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors dark:bg-purple-700 dark:hover:bg-purple-800"
                :disabled="loading"
              >
                Create New Page
              </button>
            </div>
          </div>

          <!-- Page Selection -->
          <div class="mb-8">
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-medium text-lg dark:text-gray-200">
                Facebook Page
              </h3>
              <button
                @click="fetchBusinessPages"
                class="text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded-lg transition-colors dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200"
                :disabled="loading"
              >
                Refresh Pages
              </button>
            </div>

            <div v-if="pages.length > 0" class="space-y-4">
              <div
                v-for="page in pages"
                :key="page.id"
                @click="selectPage(page)"
                class="p-4 border rounded-lg cursor-pointer transition-colors"
                :class="{
                  'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-700':
                    selectedPage === page.id,
                  'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500':
                    selectedPage !== page.id,
                }"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center"
                  >
                    <i
                      class="fa-solid fa-file-lines text-gray-600 dark:text-gray-300"
                    ></i>
                  </div>
                  <div>
                    <h4 class="font-medium dark:text-gray-200">
                      {{ page.name }}
                    </h4>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      ID: {{ page.id }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="p-4 border border-dashed rounded-lg text-center dark:border-gray-600"
            >
              <p class="text-gray-500 dark:text-gray-400 mb-3">
                No Facebook Pages found
              </p>
              <button
                @click="createFacebookPage"
                class="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors dark:bg-purple-700 dark:hover:bg-purple-800"
                :disabled="loading"
              >
                Create New Page
              </button>
            </div>
          </div>

          <!-- Ad Account Selection -->
          <div class="mb-8">
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-medium text-lg dark:text-gray-200">Ad Account</h3>
              <button
                @click="fetchBusinessAdAccounts"
                class="text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded-lg transition-colors dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200"
                :disabled="loading"
              >
                Refresh Accounts
              </button>
            </div>

            <div v-if="adAccounts.length > 0" class="space-y-4">
              <div
                v-for="account in adAccounts"
                :key="account.id"
                @click="selectAdAccount(account)"
                class="p-4 border rounded-lg cursor-pointer transition-colors"
                :class="{
                  'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-700':
                    selectedAdAccount === account.id,
                  'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500':
                    selectedAdAccount !== account.id,
                }"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center"
                  >
                    <i
                      class="fa-solid fa-wallet text-gray-600 dark:text-gray-300"
                    ></i>
                  </div>
                  <div class="flex-1">
                    <div class="flex justify-between items-start">
                      <h4 class="font-medium dark:text-gray-200">
                        {{ account.name }}
                      </h4>
                      <span
                        class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full dark:bg-green-900/50 dark:text-green-300"
                      >
                        {{
                          account.account_status === 1 ? "Active" : "Inactive"
                        }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      ID: {{ account.id }}
                    </p>
                    <div class="mt-2 text-sm">
                      <p class="text-gray-600 dark:text-gray-300">
                        Balance: {{ account.currency || "USD"
                        }}{{ formatCurrency(account.available_funds || 0) }}
                        <span
                          v-if="account.amount_spent"
                          class="ml-2 text-gray-500 dark:text-gray-400"
                        >
                          (Spent: {{ account.currency || "USD"
                          }}{{ formatCurrency(account.amount_spent || 0) }})
                        </span>
                      </p>
                    </div>
                    <div class="mt-3 flex gap-2">
                      <a
                        :href="getFundingLink(account.id, businessId)"
                        target="_blank"
                        class="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors dark:bg-blue-700 dark:hover:bg-blue-800"
                      >
                        Add Funds
                      </a>
                      <button
                        @click.stop="refreshAccount(account.id)"
                        class="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded transition-colors dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200"
                      >
                        Refresh Balance
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="p-4 border border-dashed rounded-lg text-center dark:border-gray-600"
            >
              <p class="text-gray-500 dark:text-gray-400 mb-3">
                No Ad Accounts found
              </p>
              <button
                @click="createAdAccount"
                class="text-sm bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors dark:bg-red-700 dark:hover:bg-red-800"
                :disabled="loading || !hasBusinessId"
              >
                Create New Ad Account
              </button>
            </div>
          </div>

          <!-- Business Account (if needed) -->
          <div
            v-if="needsBusinessId"
            class="p-4 bg-gray-50 rounded-lg dark:bg-gray-600/30"
          >
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-medium dark:text-gray-200">
                Business Account Required
              </h3>
              <span
                v-if="hasBusinessId"
                class="text-green-500 dark:text-green-400"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
              {{
                hasBusinessId
                  ? `Business ID: ${businessId}`
                  : "You need a Business Account to create Ad Accounts"
              }}
            </p>
            <button
              v-if="!hasBusinessId"
              @click="fetchBusinesses"
              class="text-sm bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition-colors dark:bg-yellow-700 dark:hover:bg-yellow-800"
              :disabled="loading"
            >
              Get Business ID
            </button>
          </div>

          <!-- Pixel Setup Section -->
          <div
            class="bg-white dark:bg-gray-700 rounded-lg shadow p-6 transition-colors mt-6"
          >
            <div class="flex items-start mb-6">
              <div class="mr-3">
                <span class="text-blue-500 dark:text-blue-400">
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </span>
              </div>
              <div class="flex-1">
                <h2 class="text-xl font-bold mb-3 dark:text-gray-200">
                  Facebook Pixel Setup
                </h2>
                <div
                  class="bg-blue-50 dark:bg-gray-600/30 p-4 dark:text-gray-300 rounded-lg mb-4"
                >
                  <p class="text-sm text-blue-800 dark:text-gray-200 mb-2">
                    The Facebook Pixel helps track conversions from your ads.
                    Follow these steps:
                  </p>
                  <ul class="list-disc list-inside space-y-1 text-sm">
                    <li>Select an existing pixel below or create a new one</li>
                    <li>Install the pixel code on your website</li>
                    <li>Set up conversion events you want to track</li>
                  </ul>
                </div>

                <!-- Pixel Selection -->
                <div class="flex items-start mb-4">
                  <div class="mr-3 mt-1">
                    <span
                      v-if="hasPixel"
                      class="text-green-500 dark:text-green-400"
                    >
                      <svg
                        class="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </span>
                    <span v-else class="text-yellow-500 dark:text-yellow-400">
                      <svg
                        class="w-6 h-6"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </span>
                  </div>
                  <div class="flex-1">
                    <h3 class="font-medium text-lg dark:text-gray-200">
                      Tracking Pixel
                    </h3>
                    <div v-if="pixels?.length > 0" class="mt-2">
                      <select
                        v-model="selectedPixel"
                        class="w-full border rounded-lg p-2.5 bg-white dark:bg-gray-600 dark:border-gray-500 dark:text-gray-200"
                      >
                        <option value="">Select a Pixel</option>
                        <option
                          v-for="pixel in pixels"
                          :key="pixel.id"
                          :value="pixel.id"
                        >
                          {{ pixel.name }} ({{ pixel.id }})
                        </option>
                      </select>
                      <p class="text-sm text-gray-600 dark:text-gray-300 mt-2">
                        Status:
                        {{ selectedPixelInfo?.status || "Not selected" }}
                      </p>
                    </div>
                    <div v-else class="mt-3">
                      <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                        No pixels found in your account
                      </p>
                      <button
                        @click="createPixel"
                        class="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors dark:bg-blue-700 dark:hover:bg-blue-800"
                        :disabled="loading"
                      >
                        Create New Pixel
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Pixel Code Instructions -->
                <div
                  v-if="selectedPixel"
                  class="mt-4 p-4 bg-gray-50 dark:bg-gray-600/20 rounded-lg"
                >
                  <div class="flex items-start">
                    <svg
                      class="w-5 h-5 mt-0.5 mr-2 text-blue-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      ></path>
                    </svg>
                    <div>
                      <h4 class="font-medium text-sm mb-2 dark:text-gray-200">
                        Pixel Setup Automation
                      </h4>
                      <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                        We'll automatically configure your Facebook Pixel (ID:
                        {{ selectedPixel }}) for optimal tracking. No manual
                        setup required.
                      </p>

                      <div class="space-y-3 text-sm">
                        <div class="flex items-start">
                          <svg
                            class="w-4 h-4 mt-0.5 mr-2 text-green-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                          <span class="text-gray-600 dark:text-gray-300"
                            >Pixel code will be injected automatically</span
                          >
                        </div>

                        <div class="flex items-start">
                          <svg
                            class="w-4 h-4 mt-0.5 mr-2 text-green-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                          <span class="text-gray-600 dark:text-gray-300"
                            >Standard events will be pre-configured</span
                          >
                        </div>

                        <div class="flex items-start">
                          <svg
                            class="w-4 h-4 mt-0.5 mr-2 text-green-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                          <span class="text-gray-600 dark:text-gray-300"
                            >Installation verification included</span
                          >
                        </div>
                      </div>

                      <div
                        class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-100 dark:border-blue-900/50"
                      >
                        <p class="text-xs text-blue-600 dark:text-blue-300">
                          <strong>Note:</strong> For advanced customization, you
                          can access pixel settings later in your
                          <a
                            href="https://business.facebook.com/events_manager2/"
                            target="_blank"
                            class="underline"
                            >Facebook Events Manager</a
                          >.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Save Settings Button -->
          <div class="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600">
            <button
              @click="saveSettings"
              class="w-full bg-main-800 hover:bg-mian-900 text-white px-6 py-3 rounded-lg font-medium transition-colors dark:bg-sub-800 dark:hover:bg-sub-900"
              :disabled="!selectedPage || !selectedAdAccount || saving"
            >
              <span v-if="saving">
                <i class="fa-solid fa-circle-notch fa-spin mr-2"></i>
                Saving...
              </span>
              <span v-else>
                <i class="fa-solid fa-save mr-2"></i>
                Save Promotion Settings
              </span>
            </button>
            <p
              v-if="saveSuccess"
              class="mt-2 text-green-600 dark:text-green-400 text-sm"
            >
              <i class="fa-solid fa-check-circle mr-1"></i>
              Settings saved successfully! The system will use these for
              automated ad creation.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="loading || isProcessingAutoPilot"
    class="absolute inset-0 flex items-center justify-center bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm z-10"
  >
    <LoadingComponent />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
/* global FB */
export default {
  name: "FacebookMarketing",
  data() {
    return {
      isProcessingAutoPilot: false,
      autoPilotEnabled: false,
      facebookExpanded: false,
      saving: false,
      saveSuccess: false,
      accessToken: "",
      userInfo: {},
      pages: [],
      adAccounts: [],
      businessId: "",
      selectedPage: "",
      selectedAdAccount: "",
      pageInfo: {},
      adAccountInfo: {},
      pixels: [],
      selectedPixel: "",
      tokenExpiry: null,
      loading: false,
      businesses: [],
      selectedBusiness: "",
      facebookData: {},
    };
  },
  props: {
    isDefault: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters(["selectedStore"]),
    isFacebookConnected() {
      return !!this.accessToken;
    },
    hasPage() {
      return this.pages?.length > 0;
    },
    hasPixel() {
      return this.pixels?.length > 0 && this.selectedPixel !== "";
    },
    hasAdAccount() {
      return this.adAccounts?.length > 0;
    },
    hasBusinessId() {
      return !!this.businessId;
    },
    needsBusinessId() {
      return !this.hasAdAccount;
    },
    requiredVariables() {
      return [
        {
          label: "Access Token",
          key: "accessToken",
          valid: !!this.accessToken,
          action: this.connectFacebook, // Action to set up if missing
        },
        {
          label: "Business ID",
          key: "businessId",
          valid: !!this.businessId,
          action: this.fetchBusinesses,
        },
        {
          label: "Facebook Page",
          key: "pageId",
          valid: !!this.selectedPage,
          action: this.fetchBusinessPages,
        },
        {
          label: "Page Access Token",
          key: "pageAccessToken",
          valid: !!this.pageInfo.access_token,
          action: this.fetchBusinessPages,
        },
        {
          label: "Ad Account ID",
          key: "adsAccountId",
          valid: !!this.selectedAdAccount,
          action: this.fetchBusinessAdAccounts, // Action to set up if missing
        },
        {
          label: "Ad Account Status",
          key: "adsAccountStatus",
          valid: this.adAccountInfo.account_status === 1, // Check if active
          action: this.fetchBusinessAdAccounts,
        },
        {
          label: "Ad Account Balance",
          key: "adsAccountBalance",
          valid: this.adAccountInfo.available_funds > 0, // Check if balance is positive
          action: () =>
            this.openFundingLink(this.adAccountInfo.id, this.businessId), // Defer execution
        },
        {
          label: "Pixel ID",
          key: "pixelId",
          valid: this.pixels?.length > 0,
          action: this.createPixel, // Action to set up if missing
        },
        {
          label: "Default Store Data",
          key: "defaultStoreData",
          valid: !this.isDefault,
          action: this.openDefaultStoreData, // Action to set up if missing
        },
      ];
    },
    selectedPixelInfo() {
      return this.pixels.find((p) => p.id === this.selectedPixel) || {};
    },
    incompleteCount() {
      return this.requiredVariables.filter((item) => !item.valid).length;
    },
  },
  methods: {
    ...mapActions(["exchangeToken", "createAds", "getFacebookData"]),
    async fetchFacebookData() {
      try {
        const response = await this.getFacebookData();
        if (response.success) {
          this.facebookData = response.data.data;
        } else {
          console.error(response.error);
          this.adAccountInfo = {};
        }
      } catch (error) {
        console.error("Error fetching Facebook data:", error);
        return {};
      }
    },
    async hydrateFacebookData(data) {
      this.businessId = data.business_id || "";
      this.selectedBusiness = data.business_id || "";
      this.selectedPage = data.page_id || "";
      this.selectedAdAccount = data.ad_account_id || "";
      this.selectedPixel = data.pixel_id || "";

      await this.loadFacebookSDK();
      await this.checkTokenValidity();
    },
    openDefaultStoreData() {
      this.$emit("open-default-store-data");
    },
    async handleAutoPilotChange() {
      try {
        const adResponse = await this.createAds();
        if (adResponse.success) {
          this.isProcessingAutoPilot = true;
          toast.success(
            adResponse.message ||
              "Your promotion settings are being processed. Changes may take a few minutes to complete.",
            {
              position: "top-right",
              duration: 5000,
            }
          );
          console.log(adResponse);
        } else {
          this.isProcessingAutoPilot = false;
          toast.error(
            adResponse.message ||
              "Failed to update auto pilot settings. Please try again.",
            {
              position: "top-right",
              duration: 5000,
            }
          );
        }
      } catch (error) {
        // Revert the switch if there's an error
        this.autoPilotEnabled = !this.autoPilotEnabled;

        // Show error toast
        toast.error("Failed to update auto pilot settings. Please try again.", {
          position: "top-right",
          duration: 5000,
        });
      } finally {
        this.isProcessingAutoPilot = false;
      }
    },
    saveSettings() {
      if (!this.selectedPage || !this.selectedAdAccount) return;
      this.saving = true;
      const integrationData = {
        accessToken: this.accessToken,
        fbUserId: this.userInfo.id,
        businessId: this.selectedBusiness,
        pageId: this.selectedPage,
        pageAccessToken: this.pageInfo.access_token,
        adAccountId: this.selectedAdAccount,
        pixelId: this.selectedPixel,
      };
      this.$emit("save-facebook-settings", integrationData);
      this.saveSuccess = true;
      setTimeout(() => {
        this.saveSuccess = false;
      }, 3000);
      this.saving = false;
    },
    async createPixel() {
      this.loading = true;
      try {
        const response = await fetch(
          `https://graph.facebook.com/v23.0/${this.selectedAdAccount}/adspixels`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: "My Conversion Pixel",
              access_token: this.accessToken,
            }),
          }
        );
        const data = await response.json();
        if (data.id) {
          await this.fetchPixels();
          this.selectedPixel = data.id;
        }
      } catch (error) {
        console.error("Error creating pixel:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadFacebookSDK() {
      return new Promise((resolve) => {
        if (window.FB) {
          resolve();
          return;
        }

        window.fbAsyncInit = function () {
          FB.init({
            appId: "817785907489063",
            cookie: true,
            xfbml: true,
            version: "v23.0",
          });
          resolve();
        };

        (function (d, s, id) {
          const fjs = d.getElementsByTagName(s)[0];
          if (d.getElementById(id)) return;
          const js = d.createElement(s);
          js.id = id;
          js.src = "https://connect.facebook.net/en_US/sdk.js";
          fjs.parentNode.insertBefore(js, fjs);
        })(document, "script", "facebook-jssdk");
      });
    },

    async checkTokenValidity() {
      const storedToken =
        this.facebookData.access_token ||
        localStorage.getItem("fb_longLivedToken");
      const tokenExpiry =
        this.facebookData.token_expires_at ||
        localStorage.getItem("fb_tokenExpiry");

      if (storedToken && tokenExpiry && Date.now() < parseInt(tokenExpiry)) {
        // Token is valid, skip FB.getLoginStatus and directly use it
        this.accessToken = storedToken;
        this.fetchUserInfo();
        this.fetchBusinesses();
        this.fetchBusinessPages();
        this.fetchBusinessAdAccounts();
        this.fetchPixels();
        return; // Done
      }
      this.checkLoginStatus();
    },

    checkLoginStatus() {
      if (window.FB) {
        FB.getLoginStatus((response) => {
          if (response.status === "connected") {
            this.accessToken = response.authResponse.accessToken;
            this.fetchUserInfo();
            this.fetchBusinesses();
            this.fetchBusinessPages();
            this.fetchBusinessAdAccounts();
            this.fetchPixels();
          }
        });
      }
    },

    async connectFacebook() {
      this.loading = true;
      toast.info("Connecting to facebook...");
      try {
        const response = await new Promise((resolve) => {
          FB.login(resolve, {
            scope:
              "pages_manage_metadata,ads_management,ads_read,business_management",
          });
        });

        if (response.authResponse) {
          const longLivedToken = await this.exchangeToken({
            accessToken: response.authResponse.accessToken,
          });

          if (!longLivedToken.success) {
            toast.error(
              longLivedToken.message ||
                "Failed to exchange token. Please try again."
            );
            this.loading = false;
            return;
          }

          this.accessToken =
            longLivedToken.data.accessToken ||
            response.authResponse.accessToken;

          localStorage.setItem(
            "fb_longLivedToken",
            longLivedToken.data.accessToken
          );

          localStorage.setItem("fb_tokenExpiry", Date.now() + 5184000000);

          // Run all fetches in parallel
          await Promise.all([
            this.fetchUserInfo(),
            this.fetchBusinesses(),
            this.fetchBusinessPages(),
            this.fetchBusinessAdAccounts(),
            this.fetchPixels(),
          ]);

          toast.info("Fetched all data successfully!");

          this.facebookExpanded = true;
          toast.success("Facebook connected successfully!");
        } else {
          console.error("User cancelled login or did not authorize.");
        }
      } catch (error) {
        console.error("Login error:", error);
      } finally {
        this.loading = false;
      }
    },
    async disconnectAccount() {
      this.loading = true;

      try {
        // 1. Get the long-lived token
        const longLivedToken = localStorage.getItem("fb_longLivedToken");

        // 2. Attempt to revoke the token if it exists
        if (longLivedToken) {
          try {
            const response = await fetch(
              `https://graph.facebook.com/v23.0/me/permissions?access_token=${longLivedToken}`,
              { method: "DELETE" }
            );

            const result = await response.json();

            if (!response.ok) {
              console.warn(
                "Token revocation failed:",
                result.error?.message || "Unknown error"
              );
              // Continue with local cleanup even if revocation fails
            }
          } catch (apiError) {
            console.warn("Token revocation API call failed:", apiError.message);
          }
        }

        // 3. Clear all local data
        localStorage.removeItem("fb_longLivedToken");
        localStorage.removeItem("fb_tokenExpiry");
        localStorage.removeItem("fb_userInfo");

        // 4. Reset component state
        this.accessToken = "";
        this.userInfo = {};
        this.pages = [];
        this.adAccounts = [];
        this.businessId = "";
        this.selectedPage = "";
        this.selectedAdAccount = "";
        this.saveSuccess = false;

        // 5. Attempt FB.logout() if SDK is available
        if (typeof FB !== "undefined") {
          try {
            await new Promise((resolve) => FB.logout(resolve));
          } catch (fbError) {
            console.warn("FB.logout failed:", fbError.message);
          }
        }

        // 6. Show success feedback
        toast.success("Account disconnected successfully");
      } catch (error) {
        console.error("Disconnect error:", error.message);

        toast.error("Disconnect failed. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    selectBusiness(business) {
      this.selectedBusiness = business.id;
      this.fetchBusinessAdAccounts();
    },

    selectPage(page) {
      this.selectedPage = page.id;
      this.pageInfo = page;
    },

    selectAdAccount(account) {
      this.selectedAdAccount = account.id;
      this.adAccountInfo = account;
    },

    async fetchUserInfo() {
      try {
        const userInfo = await new Promise((resolve, reject) => {
          FB.api(
            "/me",
            { fields: "id,name,email", access_token: this.accessToken },
            (response) => {
              if (response.error) reject(response.error);
              else resolve(response);
            }
          );
        });
        this.userInfo = userInfo;
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    },

    // add this tiny helper once in your component
    fbApi(path, params = {}) {
      return new Promise((resolve, reject) => {
        FB.api(path, params, (res) =>
          res && !res.error
            ? resolve(res)
            : reject(res?.error || new Error("FB.api error"))
        );
      });
    },

    async fetchPixels() {
      if (!this.selectedAdAccount) {
        console.error("No ad account selected.");
        return;
      }
      this.loading = true;
      try {
        const response = await fetch(
          `https://graph.facebook.com/v23.0/${this.selectedAdAccount}/adspixels?access_token=${this.accessToken}`
        );
        const data = await response.json();
        this.pixels = data.data || [];

        if (
          this.pixels.length > 0 &&
          !this.pixels.some((p) => p.id === this.selectedPixel)
        ) {
          this.selectedPixel = "";
          this.selectedPixelInfo = null;

          this.selectedPixel = this.pixels[0].id;
          this.selectedPixelInfo = this.pixels[0];
        } else {
          this.selectedPixelInfo =
            this.pixels.find((p) => p.id === this.selectedPixel) || {};
        }
      } catch (error) {
        console.error("Error fetching pixels:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchBusinesses() {
      this.loading = true;
      try {
        const response = await fetch(
          `https://graph.facebook.com/v23.0/me/businesses?access_token=${this.accessToken}`
        );
        const data = await response.json();
        this.businesses = data.data || [];
        if (this.businesses.length > 0 && !this.selectedBusiness) {
          this.selectedBusiness =
            this.facebookData.business_id || this.businesses[0].id;
          this.businessId =
            this.facebookData.business_id || this.businesses[0].id;
        }
      } catch (error) {
        console.error("Error fetching businesses:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchBusinessAdAccounts() {
      if (!this.selectedBusiness) return;
      this.loading = true;
      try {
        const fields =
          "id,name,account_status,amount_spent,balance,currency,spend_cap,disable_reason,funding_source,funding_source_details";
        const params = { fields, limit: 200, access_token: this.accessToken };

        const [owned, client] = await Promise.all([
          this.fbApi(`/${this.selectedBusiness}/owned_ad_accounts`, params),
          this.fbApi(`/${this.selectedBusiness}/client_ad_accounts`, params),
        ]);

        const raw = [...(owned?.data || []), ...(client?.data || [])];
        const uniqueById = Object.values(
          raw.reduce((acc, a) => ((acc[a.id] = a), acc), {})
        );

        const toNumber = (v) => {
          if (v == null) return 0;
          const n = typeof v === "string" ? Number(v) : v;
          return Number.isFinite(n) ? n : 0;
        };

        const parseDisplayBalance = (s) => {
          if (!s || typeof s !== "string") return null;
          // e.g. "Available balance (NGN1,000.01)" -> 1000.01
          const m = s.match(/([0-9][0-9,]*\.?[0-9]*)/);
          if (!m) return null;
          return Number(m[1].replace(/,/g, ""));
        };

        this.adAccounts = uniqueById.map((a) => {
          const amount_spent = toNumber(a.amount_spent);
          const spend_cap = toNumber(a.spend_cap);
          const balance_due = toNumber(a.balance);

          // 1) Prefer API-provided available_funds if present on your object
          let available =
            a.available_funds != null ? toNumber(a.available_funds) : null;

          // 2) Try to parse wallet/prepaid balance from display_string
          if (available == null) {
            const parsed = parseDisplayBalance(
              a?.funding_source_details?.display_string
            );
            if (parsed != null) available = parsed;
          }

          // 3) Fallback: compute from spend cap if there is one (> 0)
          if (available == null && spend_cap > 0) {
            available = spend_cap - amount_spent + balance_due;
          }

          // Final safety: clamp and round
          const available_funds = Math.max(
            0,
            Number((available ?? 0).toFixed(2))
          );

          return {
            ...a,
            amount_spent, // NGN
            spend_cap, // NGN
            balance: balance_due, // NGN (amount due)
            available_funds, // NGN (what you want to show users)
            spending_limit_label: spend_cap === 0 ? "Unlimited" : spend_cap,
          };
        });

        console.log("Fetched Ad Accounts:", this.adAccounts);

        const stillExists = this.adAccounts.some(
          (a) => a.id === this.selectedAdAccount
        );
        if (!stillExists) {
          const firstActive =
            this.adAccounts.find((a) => a.account_status === 1) ||
            this.adAccounts[0];
          if (firstActive) {
            this.selectedAdAccount = firstActive.id;
            this.adAccountInfo = firstActive;
          } else {
            this.selectedAdAccount = "";
            this.adAccountInfo = {};
          }
        } else {
          this.adAccountInfo =
            this.adAccounts.find((a) => a.id === this.selectedAdAccount) || {};
        }
      } catch (err) {
        console.error("Error fetching business ad accounts:", err);
      } finally {
        this.loading = false;
      }
    },

    async fetchBusinessPages() {
      if (!this.selectedBusiness) return;
      this.loading = true;
      try {
        const res = await fetch(
          `https://graph.facebook.com/v23.0/${this.selectedBusiness}/owned_pages?fields=id,name,access_token&access_token=${this.accessToken}`
        );
        const data = await res.json();
        this.pages = data.data || [];

        if (
          this.pages.length > 0 &&
          !this.pages.some((p) => p.id === this.selectedPage)
        ) {
          this.selectedPage = this.pages[0].id;
          this.pageInfo = this.pages[0];
        } else {
          this.pageInfo =
            this.pages.find((p) => p.id === this.selectedPage) || {};
          console.log("pageInfo:", this.pageInfo);
        }
      } catch (err) {
        console.error("Error fetching business pages:", err);
      } finally {
        this.loading = false;
      }
    },

    getAccountStatus(statusCode) {
      const statusMap = {
        1: "Active",
        2: "Disabled",
        3: "Unsettled",
        7: "Pending Risk Review",
        8: "Pending Settlement",
        9: "In Grace Period",
        100: "Pending Closure",
        101: "Closed",
        201: "Any Active",
        202: "Any Closed",
      };
      return statusMap[statusCode] || `Status: ${statusCode}`;
    },

    formatCurrency(amount) {
      return amount.toLocaleString("en-US", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getFundingLink(accountId, businessId) {
      if (!accountId || !businessId) {
        console.error("Missing accountId or businessId");
        return "#";
      }

      // Clean accountId
      const cleanAccountId = accountId.replace("act_", "");

      if (!/^\d+$/.test(cleanAccountId)) {
        console.error("Invalid accountId format (must be numeric)");
        return "#";
      }

      return `https://business.facebook.com/billing_hub/accounts/details/?business_id=${businessId}&asset_id=${cleanAccountId}&payment_account_id=${cleanAccountId}&placement=bizapp_ad_center&entrypoint=ad_account_info_view_button`;
    },

    openFundingLink(accountId, businessId) {
      if (!accountId || !businessId) {
        console.error(
          "Invalid accountId or businessId passed to openFundingLink"
        );
        return;
      }

      const url = this.getFundingLink(accountId, businessId);
      window.open(url, "_blank");
    },

    async refreshAccount(accountId) {
      try {
        const account = await new Promise((resolve, reject) => {
          FB.api(
            `/${accountId}`,
            {
              fields:
                "balance,amount_spent,account_status,spend_cap,funding_source,funding_source_details",
              access_token: this.accessToken,
            },
            (response) => {
              if (response.error) reject(response.error);
              else resolve(response);
            }
          );
        });
        const toNumber = (v) => {
          if (v == null) return 0;
          const n = typeof v === "string" ? Number(v) : v;
          return Number.isFinite(n) ? n : 0;
        };

        const parseDisplayBalance = (s) => {
          if (!s || typeof s !== "string") return null;
          // e.g. "Available balance (NGN1,000.01)" -> 1000.01
          const m = s.match(/([0-9][0-9,]*\.?[0-9]*)/);
          if (!m) return null;
          return Number(m[1].replace(/,/g, ""));
        };

        // Update the account in the list
        this.adAccounts = this.adAccounts.map((acc) => {
          if (acc.id === accountId) {
            const amount_spent = toNumber(acc.amount_spent);
            const spend_cap = toNumber(acc.spend_cap);
            const balance_due = toNumber(acc.balance);

            // 1) Prefer API-provided available_funds if present on your object
            let available =
              acc.available_funds != null
                ? toNumber(acc.available_funds)
                : null;

            // 2) Try to parse wallet/prepaid balance from display_string
            if (available == null) {
              const parsed = parseDisplayBalance(
                acc?.funding_source_details?.display_string
              );
              if (parsed != null) available = parsed;
            }

            // 3) Fallback: compute from spend cap if there is one (> 0)
            if (available == null && spend_cap > 0) {
              available = spend_cap - amount_spent + balance_due;
            }

            // Final safety: clamp and round
            const available_funds = Math.max(
              0,
              Number((available ?? 0).toFixed(2))
            );

            return {
              ...acc,
              balance: account.balance ? account.balance / 100 : acc.balance,
              amount_spent: account.amount_spent
                ? account.amount_spent / 100
                : acc.amount_spent,
              account_status: account.account_status || acc.account_status,
              available_funds,
            };
          }
          return acc;
        });

        // Update selected account if needed
        if (this.selectedAdAccount === accountId) {
          this.adAccountInfo = this.adAccounts.find(
            (acc) => acc.id === accountId
          );
        }
      } catch (error) {
        console.error("Error refreshing account:", error);
      }
    },

    async createFacebookPage() {
      window.open("https://www.facebook.com/pages/creation", "_blank");
    },

    async createAdAccount() {
      window.open(
        "https://business.facebook.com/settings/ad-accounts",
        "_blank"
      );
    },
  },
  watch: {
    facebookData: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.hydrateFacebookData(newVal);
      },
    },
    selectedAdAccount(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.adAccountInfo =
          this.adAccounts.find((account) => account.id === newVal) || {};
        this.fetchPixels();
      }
    },
    selectedPixel(newVal) {
      if (newVal && Array.isArray(this.pixels)) {
        this.selectedPixelInfo = this.pixels.find((p) => p.id === newVal);
      } else {
        this.selectedPixelInfo = null;
      }
    },
    selectedBusiness(newVal) {
      if (newVal) {
        this.selectedPage = null;
        this.selectedPixel = null;
        this.adAccountInfo = {};

        this.fetchBusinessPages();
        this.fetchBusinessAdAccounts();
      }
    },
  },
  async mounted() {
    await this.fetchFacebookData();
    await this.loadFacebookSDK();
    await this.checkTokenValidity();

    // Set default ad account if none is selected
    if (!this.selectedAdAccount && this.adAccounts.length > 0) {
      this.selectAdAccount(this.adAccounts[0]);
    }

    // Set default page if none is selected
    if (!this.selectedPage && this.pages.length > 0) {
      this.selectPage(this.pages[0]);
    }

    // Set default pixel if none is selected
    if (!this.selectedPixel && this.pixels.length > 0) {
      this.selectedPixel = this.pixels[0].id;
      this.selectedPixelInfo = this.pixels[0];
    }
  },
};
</script>

<style scoped>
button,
.cursor-pointer {
  transition: all 0.2s ease;
}

/* Card hover effects */
.cursor-pointer:hover {
  transform: translateY(-1px);
}
</style>
